import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import { useAuth } from '../../context/AuthContext'
import { usePermissions } from './usePermissions'

export interface BankDetail {
  id: string
  organizationId: string
  accountName: string
  accountNumber: string
  bankName: string
  branch: string
  ifsc: string
  upi: string
  accountType: string
  currency: string
  isDefault: boolean
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface BankDetailFilters {
  isActive?: boolean
  search?: string
}

export function useBankDetails(filters?: BankDetailFilters) {
  const { organization } = useAuth()
  const { can } = usePermissions()
  return useQuery({
    queryKey: ['bankDetails', organization?.id, filters],
    queryFn: async () => {
      const params = new URLSearchParams({ organizationId: organization?.id || '' })
      if (filters?.isActive !== undefined) params.set('isActive', String(filters.isActive))
      if (filters?.search) params.set('search', filters.search)
      const res = await api.get<any>(`/master-data/bank-details?${params}`)
      return (res?.data || []) as BankDetail[]
    },
    enabled: !!organization?.id && can('QUOTATIONS', 'VIEW'),
    staleTime: 5 * 60 * 1000,
  })
}

export function useBankDetailMutations() {
  const { organization } = useAuth()
  const queryClient = useQueryClient()
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['bankDetails', organization?.id] })
  }

  const create = useMutation({
    mutationFn: (payload: Omit<BankDetail, 'id' | 'createdAt' | 'updatedAt' | 'organizationId'>) =>
      api.post<any>('/master-data/bank-details', { ...payload, organizationId: organization?.id }, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const update = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<BankDetail> }) =>
      api.patch<any>(`/master-data/bank-details/${id}`, payload, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const setStatus = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.patch<any>(`/master-data/bank-details/${id}/status`, { isActive }, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.delete<any>(`/master-data/bank-details/${id}`, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const setDefault = useMutation({
    mutationFn: (id: string) =>
      api.post<any>(`/master-data/bank-details/${id}/set-default`, {}, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  return { create, update, setStatus, remove, setDefault }
}